/**
 * Droit des sociétés — Profil · Registres · AGE/AGO · Coffre-fort · Chatbot juridique.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInputFR } from "@/components/ui/date-input-fr";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Building2, FileText, Sparkles, Send, Upload, Loader2, Check, X, Save, Plus, Download, Trash2, Scale, Archive, Bot, Users, Users2, Rocket, ChevronLeft, TrendingUp, BookOpen, Gavel, Lock, MessageSquare, Bell } from "lucide-react";
import { APP_NAME } from "@/lib/branding";
import { toast } from "sonner";
import DirigeantsPanel from "@/components/legal/DirigeantsPanel";
import AssociesPanel from "@/components/legal/AssociesPanel";
import AssembliesPanel from "@/components/legal/AssembliesPanel";
import OcrExtractCard from "@/components/legal/OcrExtractCard";
import ContextEngineCard from "@/components/legal/ContextEngineCard";
import BilanSyncCard from "@/components/legal/BilanSyncCard";
import { LabelWithHistory } from "@/components/legal/FieldHistoryPopover";
import WeeklyDigestCard from "@/components/legal/WeeklyDigestCard";
import LegalOnboardingWizard, { useOnboardingStatus } from "@/components/legal/LegalOnboardingWizard";
import LegalRulesCard from "@/components/legal/LegalRulesCard";
import LegalTemplatesPanel from "@/components/legal/LegalTemplatesPanel";
import LegalBrandingCard from "@/components/legal/LegalBrandingCard";
import { LegalHub } from "@/components/legal/LegalHub";
import { RegistersDashboard } from "@/components/legal/RegistersDashboard";
import { LegalAiChat } from "@/components/legal/LegalAiChat";
import { LegalImportWizard } from "@/components/legal/LegalImportWizard";
import { LegalAlertsSettings } from "@/components/legal/LegalAlertsSettings";
import { LegalVaultHub } from "@/components/legal/LegalVaultHub";

const VIEW_TITLES = { accueil: "Droit des sociétés", avance: "Modèles & identité visuelle" };
const NAV_MAP = { societe: "profil", creer: "age", assemblees: "ago", avance: "avance" };
const MAIN_TABS = [
  { key: "accueil", label: "Accueil", icon: TrendingUp }, { key: "import", label: "Import", icon: Upload },
  { key: "profil", label: "Profil", icon: Building2 }, { key: "registres", label: "Registres", icon: BookOpen },
  { key: "ago", label: "AGO", icon: Gavel }, { key: "age", label: "AGE", icon: Scale },
  { key: "coffre", label: "Coffre-fort", icon: Lock }, { key: "chatbot", label: "Assistant", icon: MessageSquare },
  { key: "alertes", label: "Alertes", icon: Bell },
];

function RegistresTimeline({ entries, colors, labels }) {
  const byYear = {};
  for (const e of entries) {
    const y = (e.date_operation || "").slice(0, 4) || "?";
    if (!byYear[y]) byYear[y] = [];
    byYear[y].push(e);
  }
  const years = Object.keys(byYear).sort((a, b) => b.localeCompare(a));
  if (years.length === 0) {
    return <p className="text-center text-slate-500 py-8" data-testid="timeline-empty">Aucune entrée</p>;
  }
  return (
    <div className="space-y-6" data-testid="registres-timeline">
      {years.map((y) => (
        <div key={y}>
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-bold text-slate-900 font-mono">{y}</h3>
            <div className="flex-1 border-t border-slate-200"></div>
            <Badge variant="outline" className="text-xs">{byYear[y].length} entrée(s)</Badge>
          </div>
          <div className="border-l-2 border-slate-200 ml-3 pl-4 space-y-3">
            {byYear[y]
              .sort((a, b) => (b.date_operation || "").localeCompare(a.date_operation || ""))
              .map((e) => (
              <div key={e.id} className="relative" data-testid={`timeline-entry-${e.id}`}>
                <div className="absolute -left-[22px] top-1 w-3 h-3 rounded-full bg-white border-2 border-slate-400"></div>
                <div className="flex items-start gap-2 flex-wrap">
                  <Badge variant="outline" className={`${colors[e.type] || ""} text-xs`}>
                    {labels[e.type] || e.type}
                  </Badge>
                  <span className="text-xs font-mono text-slate-500">{e.date_operation}</span>
                  {e.auto_sync && (
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-[10px]">
                      <Sparkles className="w-2.5 h-2.5 mr-0.5 inline" />Auto
                    </Badge>
                  )}
                </div>
                <p className="font-medium text-sm mt-1">{e.titre}</p>
                <p className="text-xs text-slate-600 mt-0.5">{e.description}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const REG_TYPE_COLOR = {
  mouvements_titres: "bg-blue-100 text-blue-700 border-blue-200",
  decisions_gerant: "bg-emerald-100 text-emerald-700 border-emerald-200",
  decisions_unanimes_associes: "bg-teal-100 text-teal-700 border-teal-200",
  presence_assemblees: "bg-slate-100 text-slate-700 border-slate-200",
  conventions_reglementees: "bg-amber-100 text-amber-800 border-amber-200",
  deliberations: "bg-indigo-100 text-indigo-700 border-indigo-200",
  beneficiaires_effectifs: "bg-red-100 text-red-700 border-red-200",
  associes: "bg-cyan-100 text-cyan-700 border-cyan-200",
  personnel: "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200",
};
const REG_LABELS = {
  mouvements_titres: "Mouvements de titres",
  decisions_gerant: "Décisions du gérant",
  decisions_unanimes_associes: "Décisions unanimes associés",
  presence_assemblees: "Feuilles de présence",
  conventions_reglementees: "Conventions réglementées",
  deliberations: "Délibérations (PV d'AG)",
  beneficiaires_effectifs: "Bénéficiaires effectifs (RBE)",
  associes: "Associés",
  personnel: "Personnel (entrées/sorties)",
};
const DOC_LABELS = { statuts: "Statuts", kbis: "KBIS", pv_assemblee: "PV Assemblée",
  rapport_gestion: "Rapport de gestion", convention: "Convention", attestation: "Attestation",
  contrat_associe: "Contrat associé", autre: "Autre" };

export default function DroitSocietes() {
  const [tab, setTab] = useState(() => {
    try {
      const t = localStorage.getItem("legal:active_tab") || "accueil";
      return ["templates", "creer", "assemblees"].includes(t) ? "accueil" : t;
    } catch { return "accueil"; }
  });
  const changeTab = (t) => {
    const v = t === "societe" ? "profil" : t === "templates" ? "avance" : t === "assemblees" ? "ago" : t === "creer" ? "age" : t;
    setTab(v);
    try { localStorage.setItem("legal:active_tab", v); } catch { /* quota */ }
  };
  const [intent, setIntent] = useState(null);
  const [chatSeed, setChatSeed] = useState(null);
  const handleIntent = (it) => {
    if (it.action === "create_age" || it.action === "update_statuts") { changeTab("age"); setIntent(it); }
    else if (it.action === "create_ago") { changeTab("ago"); }
    else if (it.action === "upload") { changeTab("import"); }
    else if (it.action === "navigate") { changeTab(NAV_MAP[it.view] || it.view); }
    else { changeTab("chatbot"); setChatSeed({ message: it.message, nonce: it.nonce }); }
  };
  const [regView, setRegView] = useState(() => {
    try { return localStorage.getItem("legal:regs_view") || "list"; }
    catch { return "list"; }
  });
  const setRegViewPersist = (v) => {
    setRegView(v);
    try { localStorage.setItem("legal:regs_view", v); } catch { /* */ }
  };
  const [docSearchQuery, setDocSearchQuery] = useState("");
  const [docSearchResults, setDocSearchResults] = useState(null);
  const [docSearchLoading, setDocSearchLoading] = useState(false);
  const runDocSearch = async () => {
    const q = docSearchQuery.trim();
    if (q.length < 2) { toast.error("Requête trop courte (min 2 caractères)"); return; }
    setDocSearchLoading(true);
    try {
      const r = await api.get(`/legal/documents/search?q=${encodeURIComponent(q)}`);
      setDocSearchResults(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setDocSearchLoading(false); }
  };
  const [profile, setProfile] = useState(null);
  const [conformity, setConformity] = useState(null);
  const [registers, setRegisters] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [regForm, setRegForm] = useState({ type: "decisions_gerant", date_operation: "", titre: "", description: "" });
  const [showRegForm, setShowRegForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [regFilter, setRegFilter] = useState(null);
  const { open: onbOpen, setOpen: setOnbOpen, refresh: refreshOnb } = useOnboardingStatus();

  const syncAll = async () => {
    try { const r = await api.post("/legal/registers/sync"); toast.success(`Registres synchronisés (${r.data.total_created ?? r.data.created ?? 0} inscription(s) ajoutée(s))`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const load = async () => {
    try {
      const [p, c, r, d] = await Promise.all([
        api.get("/legal/profile"), api.get("/legal/conformity-score"),
        api.get("/legal/registers"), api.get("/legal/documents"),
      ]);
      setProfile(p.data); setConformity(c.data);
      setRegisters(r.data.entries || []);
      setDocuments(d.data.documents || []);
      setRefreshKey((k) => k + 1);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const saveProfile = async () => {
    try {
      await api.put("/legal/profile", profile);
      toast.success("Profil enregistré");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const addRegister = async () => {
    try {
      await api.post("/legal/registers", regForm);
      toast.success("Entrée ajoutée");
      setShowRegForm(false);
      setRegForm({ type: "decisions_gerant", date_operation: "", titre: "", description: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadDoc = async (e, category = "autre") => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/legal/documents/upload?category=${category}`, fd);
      toast.success("Document ajouté au coffre-fort");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const shownRegs = regFilter ? registers.filter((r) => r.type === regFilter) : registers;
  if (!profile) return <div className="p-6 text-slate-500">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="legal-page">
      <LegalOnboardingWizard open={onbOpen} onOpenChange={setOnbOpen} onDone={() => { refreshOnb(); load(); }} />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-lg flex items-center justify-center text-white"><Scale className="w-5 h-5" /></span>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>{VIEW_TITLES[tab] || "Droit des sociétés"}</h1>
            <p className="text-sm text-slate-500">{profile.denomination} · {profile.forme_juridique}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setOnbOpen(true)} data-testid="reopen-onboarding-btn"><Rocket className="w-3 h-3 mr-1" />Mise en route</Button>
          <Button variant="ghost" size="sm" onClick={() => changeTab("avance")} data-testid="legal-advanced-btn">Modèles</Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="grid grid-cols-5 sm:grid-cols-9 h-auto w-full" data-testid="legal-main-tabs">
          {MAIN_TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} title={t.label} data-testid={`tab-${t.key}`}
              className={`gap-1 ${t.key === "import" ? "data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700" : "data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700"} ${t.key === "profil" && ["dirigeants", "associes"].includes(tab) ? "bg-indigo-50 text-indigo-700 shadow-sm" : ""}`}>
              <t.icon className="w-4 h-4" /><span className="hidden lg:inline">{t.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>
        {["profil", "dirigeants", "associes"].includes(tab) && (
          <TabsList className="mt-3" data-testid="societe-subnav">
            <TabsTrigger value="profil" data-testid="tab-identite"><Building2 className="w-3 h-3 mr-1" />Identité & capital</TabsTrigger>
            <TabsTrigger value="dirigeants" data-testid="tab-dirigeants"><Users className="w-3 h-3 mr-1" />Dirigeants</TabsTrigger>
            <TabsTrigger value="associes" data-testid="tab-associes"><Users2 className="w-3 h-3 mr-1" />Associés</TabsTrigger>
          </TabsList>
        )}

        <TabsContent value="accueil" className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-2" data-testid="dashboard-shortcuts">
            <Button size="sm" className="bg-gradient-to-r from-red-500 to-orange-600 text-white" onClick={() => changeTab("age")} data-testid="shortcut-new-age"><Gavel className="w-4 h-4 mr-1" />Nouvelle AGE</Button>
            <Button size="sm" variant="outline" onClick={() => changeTab("ago")} data-testid="shortcut-ago"><BookOpen className="w-4 h-4 mr-1" />AGO annuelle</Button>
            <Button size="sm" variant="outline" onClick={syncAll} data-testid="shortcut-sync"><Scale className="w-4 h-4 mr-1" />Synchroniser les registres</Button>
            <Button size="sm" variant="outline" onClick={() => changeTab("import")} data-testid="shortcut-import"><Upload className="w-4 h-4 mr-1" />Importer un document</Button>
          </div>
          <LegalHub onNavigate={(t) => changeTab(t === "assemblees" ? "ago" : t)} refreshKey={refreshKey} onChanged={load} />
        </TabsContent>

        <TabsContent value="import" className="mt-4 space-y-4">
          <LegalImportWizard onApplied={load} />
          <OcrExtractCard />
          <BilanSyncCard />
        </TabsContent>

        <TabsContent value="age" className="mt-4">
          <AssembliesPanel scope="age" onStatutsChanged={load} intent={intent} onIntentConsumed={() => setIntent(null)} />
        </TabsContent>
        <TabsContent value="ago" className="mt-4">
          <AssembliesPanel scope="ago" onStatutsChanged={load} />
        </TabsContent>
        <TabsContent value="alertes" className="mt-4">
          <LegalAlertsSettings />
        </TabsContent>

        <TabsContent value="avance" className="space-y-4 mt-4">
          <LegalBrandingCard />
          <LegalTemplatesPanel />
        </TabsContent>

        <TabsContent value="dirigeants" className="mt-4"><DirigeantsPanel /></TabsContent>
        <TabsContent value="associes" className="mt-4"><AssociesPanel /></TabsContent>

        <TabsContent value="profil" className="space-y-4 mt-4">
          <ContextEngineCard onProfileUpdated={load} />
          <WeeklyDigestCard />
          <LegalRulesCard />
          {conformity && (
            <Card>
              <CardHeader><CardTitle className="text-base">Checklist conformité</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {conformity.checks.map(c => (
                  <div key={c.key} className="flex items-center gap-2 text-sm">
                    {c.passed ? <Check className="w-4 h-4 text-emerald-600" /> : <X className="w-4 h-4 text-red-500" />}
                    <span className={c.passed ? "text-slate-700" : "text-slate-400"}>{c.label}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader><CardTitle className="text-base">Profil société</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><LabelWithHistory field="denomination" currentValue={profile.denomination} onApply={(v) => setProfile({ ...profile, denomination: v })}>Dénomination</LabelWithHistory><Input value={profile.denomination || ""} onChange={e => setProfile({ ...profile, denomination: e.target.value })} data-testid="denom-input" /></div>
              <div><LabelWithHistory field="forme_juridique" currentValue={profile.forme_juridique} onApply={(v) => setProfile({ ...profile, forme_juridique: v })}>Forme juridique</LabelWithHistory>
                <Select value={profile.forme_juridique || ""} onValueChange={v => setProfile({ ...profile, forme_juridique: v })}>
                  <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>{["SARL","SAS","SASU","SA","SNC","EURL","SCI","Autre"].map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><LabelWithHistory field="capital_social" currentValue={profile.capital_social} onApply={(v) => setProfile({ ...profile, capital_social: parseFloat(v) || 0 })}>Capital social (€)</LabelWithHistory><Input type="number" value={profile.capital_social || ""} onChange={e => setProfile({ ...profile, capital_social: parseFloat(e.target.value) || 0 })} /></div>
              <div><LabelWithHistory field="siret" currentValue={profile.siret} onApply={(v) => setProfile({ ...profile, siret: v })}>SIRET</LabelWithHistory><Input value={profile.siret || ""} onChange={e => setProfile({ ...profile, siret: e.target.value })} /></div>
              <div><LabelWithHistory field="siren" currentValue={profile.siren} onApply={(v) => setProfile({ ...profile, siren: v })}>SIREN</LabelWithHistory><Input value={profile.siren || ""} onChange={e => setProfile({ ...profile, siren: e.target.value })} /></div>
              <div><LabelWithHistory field="rcs_ville" currentValue={profile.rcs_ville} onApply={(v) => setProfile({ ...profile, rcs_ville: v })}>RCS Ville</LabelWithHistory><Input value={profile.rcs_ville || ""} onChange={e => setProfile({ ...profile, rcs_ville: e.target.value })} /></div>
              <div><LabelWithHistory field="numero_tva" currentValue={profile.numero_tva} onApply={(v) => setProfile({ ...profile, numero_tva: v })}>N° TVA</LabelWithHistory><Input value={profile.numero_tva || ""} onChange={e => setProfile({ ...profile, numero_tva: e.target.value })} /></div>
              <div><LabelWithHistory field="code_ape" currentValue={profile.code_ape} onApply={(v) => setProfile({ ...profile, code_ape: v })}>Code APE</LabelWithHistory><Input value={profile.code_ape || ""} onChange={e => setProfile({ ...profile, code_ape: e.target.value })} /></div>
              <div className="md:col-span-2"><LabelWithHistory field="siege_adresse" currentValue={profile.siege_adresse} onApply={(v) => setProfile({ ...profile, siege_adresse: v })}>Siège social</LabelWithHistory><Textarea rows={2} value={profile.siege_adresse || ""} onChange={e => setProfile({ ...profile, siege_adresse: e.target.value })} /></div>
              <div><LabelWithHistory field="date_creation" currentValue={profile.date_creation} onApply={(v) => setProfile({ ...profile, date_creation: v })}>Date création</LabelWithHistory><DateInputFR value={profile.date_creation || ""} onChange={e => setProfile({ ...profile, date_creation: e.target.value })} /></div>
              <div><LabelWithHistory field="cloture_exercice" currentValue={profile.date_cloture_exercice} onApply={(v) => setProfile({ ...profile, date_cloture_exercice: v })}>Clôture exercice (MM-DD)</LabelWithHistory><Input placeholder="12-31" value={profile.date_cloture_exercice || ""} onChange={e => setProfile({ ...profile, date_cloture_exercice: e.target.value })} /></div>
              <div><LabelWithHistory field="representant_legal" currentValue={profile.representant_legal} onApply={(v) => setProfile({ ...profile, representant_legal: v })}>Représentant légal</LabelWithHistory><Input value={profile.representant_legal || ""} onChange={e => setProfile({ ...profile, representant_legal: e.target.value })} /></div>
              <div><LabelWithHistory field="representant_titre" currentValue={profile.representant_titre} onApply={(v) => setProfile({ ...profile, representant_titre: v })}>Titre</LabelWithHistory><Input placeholder="Gérant · Président" value={profile.representant_titre || ""} onChange={e => setProfile({ ...profile, representant_titre: e.target.value })} /></div>
              <div className="md:col-span-2"><LabelWithHistory field="activite_principale" currentValue={profile.activite_principale} onApply={(v) => setProfile({ ...profile, activite_principale: v })}>Activité principale</LabelWithHistory><Textarea rows={2} value={profile.activite_principale || ""} onChange={e => setProfile({ ...profile, activite_principale: e.target.value })} /></div>
            </CardContent>
          </Card>
          <div className="flex justify-end"><Button onClick={saveProfile} data-testid="save-profile-btn"><Save className="w-4 h-4 mr-2" />Enregistrer</Button></div>
        </TabsContent>

        <TabsContent value="registres" className="space-y-4 mt-4">
          <RegistersDashboard filter={regFilter} onFilter={setRegFilter} onChanged={load} refreshKey={refreshKey} />
          <div className="flex justify-between items-center flex-wrap gap-2">
            <p className="text-sm text-slate-600">{shownRegs.length} entrée(s){regFilter ? ` · ${REG_LABELS[regFilter] || regFilter}` : " au registre"}
              {regFilter && <button className="ml-2 text-xs text-blue-600 hover:underline" onClick={() => setRegFilter(null)} data-testid="reg-filter-clear">tout afficher</button>}</p>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" data-testid="sync-registers-btn"
                onClick={async () => {
                  try {
                    const r = await api.post("/legal/registers/sync");
                    toast.success(`Synchronisation OK — ${r.data.total_created} nouvelle(s) entrée(s)`);
                    load();
                  } catch (e) { toast.error(apiError(e)); }
                }}>
                <Sparkles className="w-4 h-4 mr-1.5 text-purple-500" />Synchroniser (IA)
              </Button>
              <Button variant="outline" size="sm" data-testid="export-dossier-btn"
                onClick={async () => {
                  try {
                    const r = await api.get("/legal/export-dossier-pdf", { responseType: "blob" });
                    const url = URL.createObjectURL(r.data);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `dossier_juridique_${profile?.denomination || "societe"}.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (e) { toast.error(apiError(e)); }
                }}>
                <Download className="w-4 h-4 mr-1.5" />Exporter dossier PDF
              </Button>
              <Button onClick={() => setShowRegForm(true)} data-testid="add-reg-btn">
                <Plus className="w-4 h-4 mr-2" />Nouvelle entrée
              </Button>
              <div className="flex border rounded-md overflow-hidden" data-testid="reg-view-toggle">
                <button
                  onClick={() => setRegViewPersist("list")}
                  data-testid="reg-view-list"
                  className={`px-3 py-1 text-xs font-medium ${regView === "list" ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`}>
                  Liste
                </button>
                <button
                  onClick={() => setRegViewPersist("timeline")}
                  data-testid="reg-view-timeline"
                  className={`px-3 py-1 text-xs font-medium ${regView === "timeline" ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`}>
                  Timeline
                </button>
              </div>
            </div>
          </div>
          {regView === "timeline" ? (
            <RegistresTimeline entries={shownRegs} colors={REG_TYPE_COLOR} labels={REG_LABELS} />
          ) : (
          <div className="space-y-2">
            {shownRegs.map(r => (
              <Card key={r.id}>
                <CardContent className="p-3 flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline">{REG_LABELS[r.type] || r.type}</Badge>
                      {r.auto_sync && (
                        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-[10px]">
                          <Sparkles className="w-2.5 h-2.5 mr-0.5 inline" />Auto
                        </Badge>
                      )}
                      <span className="text-xs text-slate-500">{r.date_operation}</span>
                    </div>
                    <p className="font-medium mt-1">{r.titre}</p>
                    <p className="text-sm text-slate-600">{r.description}</p>
                    {r.linked_document_id && (
                      <button
                        onClick={async () => {
                          try {
                            const resp = await api.get(`/legal/documents/${r.linked_document_id}/download`, { responseType: "blob" });
                            const blobUrl = URL.createObjectURL(resp.data);
                            window.open(blobUrl, "_blank");
                            setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
                          } catch (e) { toast.error(apiError(e)); }
                        }}
                        className="text-xs text-blue-600 hover:underline mt-1 inline-flex items-center gap-1"
                        data-testid={`register-pdf-link-${r.id}`}>
                        <FileText className="w-3 h-3" />Voir le PDF source (coffre-fort)
                      </button>
                    )}
                  </div>
                  {!r.auto_sync && (
                    <Button variant="ghost" size="sm" onClick={() => api.delete(`/legal/registers/${r.id}`).then(load)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
            {shownRegs.length === 0 && <p className="text-center text-slate-500 py-8">Aucune entrée</p>}
          </div>
          )}
        </TabsContent>

        <TabsContent value="coffre" className="space-y-4 mt-4">
          <Card className="border-blue-200" data-testid="doc-search-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-blue-600" />
                Recherche full-text — Conventions, feuilles de présence, tous documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={(e) => { e.preventDefault(); runDocSearch(); }} className="flex gap-2">
                <Input
                  placeholder="Ex: caution personnelle, prestation dirigeant, dividendes…"
                  value={docSearchQuery}
                  onChange={(e) => setDocSearchQuery(e.target.value)}
                  data-testid="doc-search-input" />
                <Button type="submit" disabled={docSearchLoading} data-testid="doc-search-btn">
                  {docSearchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Chercher"}
                </Button>
              </form>
              {docSearchResults && (
                <div className="mt-3">
                  <p className="text-xs text-slate-500 mb-2">
                    {docSearchResults.count} résultat(s) pour « {docSearchResults.query} »
                  </p>
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {docSearchResults.matches.map((m) => (
                      <button key={m.document_id}
                        data-testid={`doc-search-hit-${m.document_id}`}
                        onClick={async () => {
                          try {
                            const resp = await api.get(`/legal/documents/${m.document_id}/download`, { responseType: "blob" });
                            const blobUrl = URL.createObjectURL(resp.data);
                            window.open(blobUrl, "_blank");
                            setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
                          } catch (e) { toast.error(apiError(e)); }
                        }}
                        className="w-full text-left p-2 border rounded hover:bg-blue-50 text-xs">
                        <div className="flex items-center gap-2">
                          <FileText className="w-3 h-3 text-blue-600" />
                          <span className="font-medium">{m.filename}</span>
                          <Badge variant="outline" className="text-[10px]">{m.category || "autre"}</Badge>
                        </div>
                        {m.excerpt && <p className="text-slate-500 mt-1 pl-5 italic">…{m.excerpt}…</p>}
                      </button>
                    ))}
                    {docSearchResults.matches.length === 0 && (
                      <p className="text-slate-500 text-sm py-4 text-center">Aucun document ne contient ce mot-clé</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          <LegalVaultHub refreshKey={refreshKey} onChanged={load} />
        </TabsContent>

        <TabsContent value="chatbot" className="space-y-4 mt-4">
          <p className="text-xs inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700" data-testid="chat-context-indicator"><Lock className="w-3 h-3" />Répond avec les données de VOTRE société : {profile.denomination}</p>
          <LegalAiChat seed={chatSeed} onSeedConsumed={() => setChatSeed(null)} />
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <Dialog open={showRegForm} onOpenChange={setShowRegForm}>
        <DialogContent><DialogHeader><DialogTitle>Nouvelle entrée registre</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Type</Label>
              <Select value={regForm.type} onValueChange={v => setRegForm({ ...regForm, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(REG_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date opération</Label><DateInputFR value={regForm.date_operation} onChange={e => setRegForm({ ...regForm, date_operation: e.target.value })} /></div>
            <div><Label>Titre</Label><Input value={regForm.titre} onChange={e => setRegForm({ ...regForm, titre: e.target.value })} /></div>
            <div><Label>Description</Label><Textarea rows={3} value={regForm.description} onChange={e => setRegForm({ ...regForm, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={addRegister} disabled={!regForm.titre || !regForm.date_operation}>Ajouter</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
